// `TrialStateMachine` — pure derivation of trial UI states.
//
// Mirrors the server-side `services/billing/trial.ts` so the UI can show
// the right banner variant before the next refetch. Exposed as both a
// hook (`useTrialState`) and a component-shaped helper (`<TrialState>`).

import { type ReactNode } from 'react';

import type { BillingResponse } from '@barel/api-client/react';

export type TrialBannerState = 'normal' | 'urgent' | 'endingToday' | 'expired';
export type AccessTier = 'full' | 'soft_block' | 'hard_block';

export interface DerivedTrialState {
  bannerState: TrialBannerState | null;
  accessTier: AccessTier;
  expired: boolean;
  daysLeft: number | null;
  status: BillingResponse['trial']['status'] | null;
}

export function deriveTrialState(data: BillingResponse | undefined): DerivedTrialState {
  if (!data) {
    return {
      bannerState: null,
      accessTier: 'full',
      expired: false,
      daysLeft: null,
      status: null,
    };
  }
  // The API computes access from subscription state plus the persisted
  // soft-block entry time. Recomputing it in the browser loses that context
  // and previously contradicted the server for users without a subscription.
  const trial = data.trial;
  return {
    bannerState: trial.bannerState,
    accessTier: trial.accessTier,
    expired: trial.expired,
    daysLeft: trial.trialDaysLeft,
    status: trial.status,
  };
}

export interface TrialStateProps {
  data: BillingResponse | undefined;
  children: (state: DerivedTrialState) => ReactNode;
}

export function TrialState({ data, children }: TrialStateProps) {
  const state = deriveTrialState(data);
  return <>{children(state)}</>;
}
